/**
 * AddContentSheet
 *
 * Helper to show the Add Content (video upload) sheet using SheetService.
 */

import React from 'react';
import { SheetService } from '@/presentation/components/common/sheet/service/SheetService.index';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { store } from '@/presentation/store';
import { showBudgetExceededOverlay } from '@/presentation/store/slices/appOverlay/appOverlaySlice';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import type { ContentReplyCommentInfoEntity } from '@/domain/entities/comment/contentReplyCommentInfoEntity';
import type { SendMessageMediaArgs } from './hooks/useSendMessageMedia';
import { AddContentSheetContent } from './components/AddContentSheetContent';

/**
 * Chat-flow discriminator + send action passed to `show`. Presence of this
 * options object drives the chat-media flow (Drop + Preview only). Pairing the
 * resolved `chatUsersInfo` with its `onSendMedia` in one sub-object makes the
 * previously-possible inconsistent state (one supplied without the other)
 * unrepresentable. `onSendMedia` is wired by `ChatComposer` to `useChat.sendMedia`
 * (Finding 3); it takes the shared `SendMessageMediaArgs` the sheet collects.
 */
export interface ChatAddContentOptions {
  chatUsersInfo: ChatUsersInfoEntity;
  onSendMedia: (args: SendMessageMediaArgs) => Promise<boolean>;
}

/**
 * Community-flow discriminator + send action passed to `show`. Presence of this
 * options object drives the community media flow (Drop + Preview only).
 * `onSendMedia` is wired by `CommunityComposer`, which routes the collected
 * `SendMessageMediaArgs` to `useCommunity.sendImage` OR `useCommunity.sendVideo`
 * by `mediaType` (Task 16); the routed feature hook owns the optimistic tile +
 * upload/persist and resolves the upload-success boolean so the sheet can toast
 * on failure. It takes the shared `SendMessageMediaArgs` (the same shape the chat
 * seam collects); the community composer bridges it by adding the tap-time `text`
 * / `capturedAtMillis` / `replyToMessage` snapshot (+ `durationSeconds` for
 * video). Community video acceptance is switched on (Task 17):
 * `resolveDropStepConfig` includes `SUPPORTED_VIDEO_ACCEPT` for the community
 * flow and `useAddContent` validates community videos against the chat
 * 5 s–5 min duration band.
 */
export interface CommunityAddContentOptions {
  onSendMedia: (args: SendMessageMediaArgs) => Promise<boolean>;
}

/**
 * Feedback-flow discriminator + pick action passed to `show`. Presence of this
 * options object drives the feedback media flow (image-only Drop + Preview).
 *
 * Unlike the chat/community `onSendMedia`, this is a **collect-not-send** seam:
 * the Preview "Done" hands the picked image back to the caller so `FeedbackPage`
 * can APPEND it to its local draft list — there is NO upload, NO confirm sheet,
 * and NO failure toast at pick time (mirroring Flutter's `_uploadFromGallery`,
 * `in_app_feedback_screen.dart:176`, which simply
 * `_screenshotAppMediaFiles.add(appMedia)`). The actual upload+persist happens
 * only when the page's own "Done" CTA is confirmed (Task 10).
 */
export interface FeedbackAddContentOptions {
  onPickImage: (screenshot: { file: File; aspectRatio: number }) => void;
}

/**
 * Profile-image-flow discriminator + pick action passed to `show`. Presence of
 * this options object drives the image-only Drop + Preview profile flow.
 *
 * Like the sibling `feedback` flow (and unlike chat/community's `onSendMedia`),
 * the Preview "Done" is a **collect-not-send** seam: it hands the picked
 * `{ file, aspectRatio }` back to `EditUserProfilePage`, which turns it into a
 * local object-URL avatar preview via `useEditUserProfile.onProfileImagePicked`
 * (Task 10). There is NO upload, NO confirm sheet, and NO failure toast at pick
 * time — mirroring Flutter's `returnLocalMedia: true, uploadOnly: true` picker
 * (`edit_user_profile_screen.dart:272-279`), whose result the screen simply
 * assigns to `_profileImage` (`:280-285`). The real upload runs later, from the
 * page's own "Done" CTA (`users_repository.dart:68-71`; web: Task 5's use case).
 * A cancelled pick is a pure no-op — `onPickImage` is simply never invoked.
 *
 * `aspectRatio` is unused by the profile caller (the avatar is a fixed circle)
 * but is carried so the field stays consistent with the sibling flows' shared
 * `SendMessageMediaArgs` shape.
 *
 * Step titles: this flow deliberately inherits the shared message-flow titles
 * (`publish.chatAddContentTitle` / `chatPreviewTitle`), exactly as the shipped
 * `feedback` flow does — minting profile-specific title keys would diverge from
 * the sibling for no behavioural gain. The *drop-step* copy IS specialised to
 * image-only (`resolveDropStepConfig`).
 */
export interface ProfileImageAddContentOptions {
  onPickImage: (image: { file: File; aspectRatio: number }) => void;
}

/**
 * Comment-reply-flow discriminator + upload-result callback passed to `show`.
 *
 * Presence of this options object drives the comment-reply variant of the
 * **full 5-step user flow** — NOT a trimmed message flow. It resolves to the
 * `AddContentFlowType.comment` member, every branch of which falls
 * through to the `user` defaults (video-only drop, `CONTENT_MIN/MAX_SECONDS`
 * band, description → settings → monetization → confirm), matching Flutter's
 * comment reply running the ordinary publish chain
 * (`add_content_screen.dart:678-706`). The only surface difference is the
 * picker step's title ("Reply comment", `comments_bottom_sheet.dart:916`,
 * forwarded to the app bar at `add_content_screen.dart:1057-1062`) — steps 2-5
 * keep their own titles.
 *
 * `contentReplyCommentInfo` is built by the caller at tap time
 * (`useCommentVideoReply`, Task 8) and rides the `transcodeUserVideo` payload
 * (Task 1) so the server can stamp the real `mediaInfo` onto the comment doc.
 *
 * `onUploaded` is the web equivalent of Flutter's awaited `bool uploaded`
 * (`comments_bottom_sheet.dart:915`) and is invoked exactly once with the
 * upload result (Task 3 fires it from `usePublishContent`). Like Flutter's, it
 * resolves at upload **kick-off**, not completion.
 */
export interface CommentReplyAddContentOptions {
  contentReplyCommentInfo: ContentReplyCommentInfoEntity;
  onUploaded: (success: boolean) => void;
}

/**
 * Options bag for `show`. Sheet-placement fields (`container` / `screenKey` /
 * `width` / `fixedHeight`) plus at most one flow discriminator: `chat` OR
 * `community` OR `feedback` OR `profileImage` OR `commentReply`. Absent all →
 * the standard user-content flow (all five steps). Replaces the former seven
 * trailing positional params (Options-Object Params convention): call sites stay
 * self-describing and every future flow variant adds a named field rather than
 * another placeholder slot.
 */
export interface AddContentSheetShowOptions {
  container?: HTMLElement | string | null;
  screenKey?: string;
  width?: string;
  fixedHeight?: string;
  /** Presence drives the chat-media flow. */
  chat?: ChatAddContentOptions;
  /** Presence drives the community-media flow (images + videos). */
  community?: CommunityAddContentOptions;
  /** Presence drives the feedback media flow (image-only, collect-not-send). */
  feedback?: FeedbackAddContentOptions;
  /** Presence drives the profile-image flow (image-only, collect-not-send). */
  profileImage?: ProfileImageAddContentOptions;
  /** Presence drives the comment-reply flow (full 5-step user flow, "Reply comment" picker title). */
  commentReply?: CommentReplyAddContentOptions;
}

/**
 * Opens the Add Content sheet for whichever flow the options select.
 *
 * **Budget-exceeded gate.** Flutter puts its gate inside the shared navigation
 * helper `NavigationService.navigateToAddContentScreen`
 * (`lib/services/navigation_service.dart:437-442`): when the ops budget is
 * exceeded it raises the budget-exceeded overlay and returns WITHOUT opening the
 * screen, so all of Flutter's add-content call sites are covered by that one
 * check. `AddContentSheet.show` is the web analog of that helper — the single
 * chokepoint every add-content entry point funnels through — so the same single
 * gate lives here and covers all six callers:
 * `SelectContentTypeSheetContent.tsx:51` (publish / user content),
 * `ChatComposer.tsx:139`, `CommunityComposer.tsx:204`, `FeedbackPage.tsx:91`,
 * `EditUserProfilePage.tsx:87` and `useCommentVideoReply.ts:124` (the
 * comment-reply flow). Per-entry-point gates are therefore neither
 * needed nor wanted — duplicating the check would let a future sixth caller ship
 * ungated, which is exactly the "an action reachable from multiple entry points
 * must be gated from all of them" failure mode (`workflow/lessons.md`).
 *
 * This is a plain function, not a React component, so it dispatches on the
 * `store` singleton rather than `useDispatch` (same escape hatch as
 * `Navigator.navigateToVideoCall`).
 *
 * Flutter checks its "Upload in progress" info sheet BEFORE the budget gate
 * (`navigation_service.dart:411-434`); the web has no upload-in-progress guard on
 * this path, so there is nothing to order against here.
 */
const show = ({
  container,
  screenKey,
  width,
  fixedHeight,
  chat,
  community,
  feedback,
  profileImage,
  commentReply,
}: AddContentSheetShowOptions = {}) => {
  // Flutter navigation_service.dart:437-442 — gate before any other work.
  if (remoteConfigService.budgetExceeded) {
    store.dispatch(showBudgetExceededOverlay());
    return;
  }

  // The chat, community, feedback, profileImage AND commentReply flows open this
  // sheet standalone (directly from their composer / page / sheet — the profile
  // flow opens it straight from `EditUserProfilePage`, the comment-reply flow
  // straight from the comments sheet), so finishing must close exactly ONE
  // sheet. Closing two would tear down the comments sheet the comment-reply
  // caller still needs. The user-content flow opens it nested under
  // `SelectContentTypeSheet`, so finishing closes BOTH the add-content sheet and
  // the select-type sheet underneath.
  const isStandalone =
    chat !== undefined
    || community !== undefined
    || feedback !== undefined
    || profileImage !== undefined
    || commentReply !== undefined;

  const SheetContent: React.FC = () => {
    return (
      <AddContentSheetContent
        onFinish={() => {
          SheetService.close();
          if (!isStandalone) {
            SheetService.close();
          }
        }}
        screenKey={screenKey}
        chat={chat}
        community={community}
        feedback={feedback}
        profileImage={profileImage}
        commentReply={commentReply}
      />
    );
  };

  SheetService.show({
    content: <SheetContent />,
    container,
    screenKey,
    width,
    fixedHeight,
    dismissible: false,
    closeOnClickOutside: false,
    scrollable: false,
  });
};

export const AddContentSheet = {
  show,
};
